import React from 'react';
import SEO from './SEO';

const Photoshoots: React.FC = () => {
  const sessionSteps = [
    {
      title: 'Arrival',
      description:
        'Arrive at The Stables at least 15 minutes before your scheduled time. We\'ll welcome you and show you and your photographer around the facilities and where you are allowed to take photos.',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Safety Briefing',
      description:
        'Your safety and the well-being of our horses are our top priorities. We\'ll provide a brief orientation on interacting with the horses and the do\'s and don\'ts during the shoot.',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'Guidance',
      description:
        'Our experienced handlers will assist you throughout the session, ensuring a harmonious experience between you, the horses, and the environment.',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: 'Capture the Magic',
      description:
        'Let the photoshoot begin! Capture the enchanting moments with our horses against the stunning backdrop of nature.',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: 'Farewell',
      description: 'After your session, bid adieu to our horses with a heart full of memories.',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
  ];

  const photographers = [
    {
      name: 'Love Like Confetti',
      note: 'On site photographer',
      contact: '069 946 4074',
      link: 'tel:+27699464074',
    },
    {
      name: 'George & Co.',
      contact: 'info@georgeandco.co.za',
      link: 'mailto:info@georgeandco.co.za',
    },
    {
      name: 'Foxline Productions',
      contact: 'belrie@foxlineproductions.co.za',
      link: 'mailto:belrie@foxlineproductions.co.za',
    },
    {
      name: 'Hazel & Kin',
      contact: 'info@hazelandkin.com',
      link: 'mailto:info@hazelandkin.com',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Horse Photoshoots Pretoria East | Equine Photography | The Stables"
        description="Book a horse photoshoot at The Stables, Pretoria East. R850 per session. Stunning backdrops, beautiful horses, and scenic farm landscapes. Enquire today!"
        path="/photoshoots"
        image="https://www.thestableshorserides.co.za/Stables2.jpg"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 via-stables-50 to-stables-100">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: "url('/Stables2.jpg')"
        }}></div>
        <div className="absolute inset-0 bg-black bg-opacity-55"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-transparent"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <p className="text-lg uppercase tracking-widest mb-3 font-light">Welcome to</p>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">The Stables</h1>
          <p className="text-xl md:text-2xl mb-8 font-light max-w-3xl mx-auto">
            Picturesque landscapes, beautiful horses, and a serene atmosphere — the perfect backdrop for capturing unforgettable moments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@thestableshorserides.co.za?subject=Horse Photoshoot Enquiry"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Enquire via Email
            </a>
            <a
              href="tel:+27699464074"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              069 946 4074
            </a>
          </div>
        </div>
      </section>

      {/* Intro & Booking */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl font-bold text-stables-800 mb-6">Photoshoot Sessions</h2>
              <p className="text-xl text-stables-700 leading-relaxed mb-8">
                With our picturesque landscapes, beautiful horses, and serene atmosphere, we provide the
                perfect backdrop for capturing unforgettable moments. To ensure your experience is smooth
                and enjoyable, here's everything you need to know about our photoshoot sessions.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2.5"></div>
                  <p className="text-stables-700"><span className="font-semibold text-stables-800">Booking:</span> Contact us to schedule your photoshoot session. Our staff will assist you in choosing the right horses and location for your shoot.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2.5"></div>
                  <p className="text-stables-700"><span className="font-semibold text-stables-800">Email:</span> <a href="mailto:info@thestableshorserides.co.za" className="text-stables-600 hover:underline">info@thestableshorserides.co.za</a></p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2.5"></div>
                  <p className="text-stables-700"><span className="font-semibold text-stables-800">Phone:</span> <a href="tel:+27699464074" className="text-stables-600 hover:underline">069 946 4074</a></p>
                </div>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="bg-white rounded-3xl p-10 shadow-xl border border-stables-200">
              <div className="text-center mb-6">
                <p className="text-stables-600 font-semibold uppercase tracking-wide text-sm mb-2">Location Pass</p>
                <p className="text-6xl font-bold text-stables-800 mb-1">R850</p>
                <p className="text-stables-600">per session</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-amber-900 text-sm font-medium text-center">
                  This is a location & horse access pass — photographer not included.
                </p>
              </div>
              <h3 className="text-xl font-bold text-stables-800 mb-4">Includes:</h3>
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2"></div>
                  <p className="text-stables-700">60-minute session — 30 minutes with a horse and 30 minutes use of designated areas around The Stables</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2"></div>
                  <p className="text-stables-700">A handler will be with you at all times to ensure everyone's safety</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2"></div>
                  <p className="text-stables-700">A few treats available for you to feed the horse during your session</p>
                </div>
              </div>
              <a
                href="mailto:info@thestableshorserides.co.za?subject=Horse Photoshoot Enquiry"
                className="block text-center bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
              >
                Enquire via Email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-stables-800 mb-12 text-center">The Setting</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="overflow-hidden rounded-3xl shadow-xl group">
              <img
                src="/aboutUs.jpg"
                alt="The Stables grounds"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-3xl shadow-xl group">
              <img
                src="/Stables2.jpg"
                alt="The Stables horses"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-3xl shadow-xl group">
              <img
                src="/landingHeroSection2.jpg"
                alt="The Stables landscape"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-12 bg-stables-700 text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <p className="text-stables-100">All dates, time slots and horses are subject to availability.</p>
            <p className="text-stables-100">Time slots are allocated seasonally.</p>
            <p className="text-stables-100">This does not include use of any other areas on Rosemary Hill Farm.</p>
          </div>
        </div>
      </section>

      {/* Session Flow */}
      <section className="py-20 bg-earth-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-stables-800 mb-4">Your Session</h2>
            <p className="text-xl text-stables-700">Here's what to expect on the day</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sessionSteps.map((step, index) => (
              <div key={index} className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-stables-200">
                <div className="bg-gradient-to-br from-stables-500 to-stables-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-stables-800 mb-3">{step.title}</h3>
                <p className="text-stables-700 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Horse Time Note */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-amber-900 text-lg leading-relaxed">
            <span className="font-bold">Please note</span> that all sessions with horses are limited to 30 minutes.
            Horses are living beings and can only be entertained for a short time while standing still — after
            30 minutes they must return to the field or stable to graze.
          </p>
        </div>
      </section>

      {/* Preferred Photographers */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-stables-800 mb-4">Preferred Photographers</h2>
            <p className="text-xl text-stables-700">We recommend these talented photographers for your session</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {photographers.map((photographer, index) => (
              <div key={index} className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-stables-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-stables-800 mb-1">{photographer.name}</h3>
                    {photographer.note && (
                      <p className="text-sm text-stables-500 italic mb-2">{photographer.note}</p>
                    )}
                    <a href={photographer.link} className="text-stables-600 hover:text-stables-700 hover:underline transition-colors">
                      {photographer.contact}
                    </a>
                  </div>
                  <div className="bg-stables-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-stables-200 transition-colors">
                    <svg className="w-5 h-5 text-stables-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-stables-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Ready to Capture Your Moment?</h2>
          <p className="text-xl mb-8">
            Get in touch to book your session and create stunning memories with our horses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@thestableshorserides.co.za?subject=Horse Photoshoot Enquiry"
              className="bg-cream-50 text-stables-800 hover:bg-cream-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Email Us
            </a>
            <a
              href="tel:+27699464074"
              className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-white hover:text-stables-600 transition-colors duration-300"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Photoshoots;
